// How the phone reads the answer to one lot-state request (BL-2681, ADR 070 — Ingest Protocol § 4.3):
// pure, status code and body in, what the lot's desired state does next out.
import { isRecord } from './change-queue-entry';
import { replayErrorOf, type ReplayError } from './change-queue-outcome';

/** What one lot-state request's answer means for the lot's state. */
export type LotStateOutcome =
  /** It landed, or had already landed (`repeat`). */
  | { kind: 'synced'; repeat: boolean }
  /** `STALE_FIELDS`: someone else changed a field this write changes. Overwrite or Keep. */
  | { kind: 'refused'; error: ReplayError }
  /** The server said no for its own reason, or the lot id is taken. Retry or Discard. */
  | { kind: 'failed'; error: ReplayError }
  /** A server error: the lot stays queued and is sent again on a later pass. */
  | { kind: 'retry-later'; error: ReplayError }
  /** The session expired: stop the pass and leave the lot as it was. */
  | { kind: 'transport'; reason: 'unauthorized' };

/**
 * Classify one lot-state request's answer.
 *
 * @param input.status - the HTTP status.
 * @param input.body - the parsed JSON body, or null when there was none.
 * @returns what the queue does with the lot's state.
 */
export function classifyStateResponse(input: { status: number; body: unknown }): LotStateOutcome {
  const { status } = input;
  const body = isRecord(input.body) ? input.body : {};
  if (status >= 200 && status < 300) {
    return { kind: 'synced', repeat: isRecord(body.data) && body.data.repeat === true };
  }
  if (status === 401) return { kind: 'transport', reason: 'unauthorized' };
  if (status === 409 && body.code === 'STALE_FIELDS') {
    const fieldNames = Array.isArray(body.staleFields) ? body.staleFields.filter((f): f is string => typeof f === 'string') : [];
    return { kind: 'refused', error: { ...replayErrorOf(status, body), fieldNames } };
  }
  if (status >= 500) return { kind: 'retry-later', error: replayErrorOf(status, body) };
  return { kind: 'failed', error: replayErrorOf(status, body) };
}
